import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Truck, 
  Target, 
  Users, 
  Calendar, 
  Phone, 
  Mail, 
  ChevronRight,
  TrendingUp,
  Shield,
  Award,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo MOBILSAFE inline
const MOBILSAFE_LOGO = "/logo-mobilsafe-claro.png";

const InformeCAES = () => {
  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeSpentRef = useRef(0);
  const scrollTrackedRef = useRef(0);
  const lastTrackTimeRef = useRef(Date.now());
  
  // Obtener ID del informe de la URL
  const informeId = window.location.pathname.split('/informe/')[1]?.replace('/', '');
  
  // Función para trackear eventos
  const trackEvent = useCallback(async (evento, datos = {}) => {
    if (!informeId) return;
    try {
      await fetch(`${API}/informe/${informeId}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ evento, ...datos })
      });
    } catch (e) {
      console.error('Error tracking:', e);
    }
  }, [informeId]);
  
  // Cargar informe
  useEffect(() => {
    const fetchInforme = async () => {
      if (!informeId) {
        setError('ID de informe no válido');
        setLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API}/informe/${informeId}`);
        if (!res.ok) throw new Error('Informe no encontrado');
        const data = await res.json();
        setInforme(data);
        
        // Trackear apertura
        trackEvent('opened');
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInforme();
  }, [informeId, trackEvent]);
  
  // Trackear tiempo en página (cada 30 segundos)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - lastTrackTimeRef.current) / 1000);
      if (elapsed >= 30) {
        trackEvent('time_spent', { tiempo_segundos: elapsed });
        lastTrackTimeRef.current = now;
        timeSpentRef.current += elapsed;
      }
    }, 30000);
    
    // Trackear al salir
    const handleUnload = () => {
      const elapsed = Math.floor((Date.now() - lastTrackTimeRef.current) / 1000);
      if (elapsed > 0) {
        navigator.sendBeacon(`${API}/informe/${informeId}/track`, JSON.stringify({
          evento: 'time_spent',
          tiempo_segundos: elapsed
        }));
      }
    };
    
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [informeId, trackEvent]);
  
  // Trackear scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPct = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      // Trackear en incrementos de 25%
      if (scrollPct >= scrollTrackedRef.current + 25) {
        scrollTrackedRef.current = Math.floor(scrollPct / 25) * 25;
        trackEvent('scroll', { scroll_pct: scrollTrackedRef.current });
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackEvent]);
  
  // Manejar clic en CTA
  const handleCTAClick = () => {
    trackEvent('cta_clicked');
    // Abrir teléfono si está disponible
    if (informe?.comercial_email) {
      window.location.href = `mailto:${informe.comercial_email}?subject=Solicitud de reunión CAES - ${informe.empresa}`;
    }
  };
  
  // Formatear números
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-ES').format(Math.round(num));
  };
  
  // Nombres de sector
  const sectorNames = {
    'urbano': 'Transporte Urbano',
    'interurbano': 'Transporte Interurbano',
    'distribucion': 'Distribución Última Milla',
    'logistica': 'Logística Larga Distancia',
    'taxi': 'Taxi / VTC'
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando informe...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center bg-slate-800 p-8 rounded-2xl max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-white mb-2">Informe no encontrado</h2>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }
  
  const rangoMobilsafe = informe.rangoMax * 1.5;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-emerald-500/20 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-8" />
          <span className="text-emerald-400 text-sm font-medium">Informe Personalizado CAES</span>
        </div>
      </header>
      
      {/* Hero */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            Informe preparado para {informe.empresa}
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            Tu flota tiene dinero dormido.<br />
            <span className="text-emerald-400">Te enseñamos cuánto.</span>
          </h1>
          <p className="text-slate-400 text-lg">
            Basado en los datos que nos has proporcionado
          </p>
        </div>
      </section>
      
      {/* Datos del cliente */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-400" />
              Tu Situación Actual
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{informe.numVehicles}</div>
                <div className="text-slate-400 text-sm">Vehículos</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{formatNumber(informe.kmAnuales)}</div>
                <div className="text-slate-400 text-sm">Km/año</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{informe.consumoMedio}</div>
                <div className="text-slate-400 text-sm">L/100km</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{informe.pctAhorro}%</div>
                <div className="text-slate-400 text-sm">Ahorro actual</div>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <span className="inline-block bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-lg text-sm">
                Sector: {sectorNames[informe.sector] || informe.sector}
              </span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Comparativa: Mercado vs MOBILSAFE */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Lo que el mercado te ofrece<br />
            <span className="text-amber-500">vs lo que NOSOTROS te damos</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Mercado */}
            <div className="bg-slate-800/50 rounded-2xl border border-slate-600/50 p-6">
              <div className="text-center mb-6">
                <div className="text-slate-400 text-sm uppercase tracking-wide mb-2">Gestor estándar</div>
                <div className="text-3xl md:text-4xl font-black text-white">
                  {formatNumber(informe.rangoMin)}€ - {formatNumber(informe.rangoMax)}€
                </div>
                <div className="text-slate-500 text-sm mt-1">en certificados CAES</div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-slate-500 mt-1">•</span>
                  <span>Toman datos y tramitan papeles</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-slate-500 mt-1">•</span>
                  <span>Te dejan solo tras la firma</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-slate-500 mt-1">•</span>
                  <span>Resultados estándar del mercado</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-slate-500 mt-1">•</span>
                  <span>Sin seguimiento de conducción</span>
                </li>
              </ul>
            </div>
            
            {/* MOBILSAFE */}
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 rounded-2xl border-2 border-amber-500/50 p-6 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                +50% MÁS
              </div>
              
              <div className="text-center mb-6">
                <div className="text-amber-400 text-sm uppercase tracking-wide mb-2">Con MOBILSAFE</div>
                <div className="text-3xl md:text-4xl font-black text-white">
                  Hasta {formatNumber(rangoMobilsafe)}€
                </div>
                <div className="text-amber-400/70 text-sm mt-1">en certificados CAES</div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Mejoramos tu conducción</strong> - No solo tramitamos</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong>90 días de acompañamiento real</strong></span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Formación continua</strong> a conductores</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Acuerdos marco</strong> con sujetos obligados</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Nuestra Metodología */}
      <section className="py-12 px-6 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            Nuestra Metodología
          </h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
            No solo tramitamos CAES, te ayudamos a generar más ahorro real
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Auditoría Inicial</h3>
              <p className="text-slate-400 text-sm">
                Analizamos tus patrones de conducción actuales para identificar oportunidades de mejora.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Plan Personalizado</h3>
              <p className="text-slate-400 text-sm">
                Diseñamos un plan de mejora adaptado a tu flota y sector específico.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Seguimiento Continuo</h3>
              <p className="text-slate-400 text-sm">
                90 días de acompañamiento con medición de resultados y ajustes.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Optimización de Venta</h3>
              <p className="text-slate-400 text-sm">
                Acuerdos marco con sujetos obligados para maximizar el valor de tus certificados.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Final */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-emerald-500/20 to-green-600/20 rounded-3xl border border-emerald-500/30 p-8 md:p-12">
            <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿15 minutos para explicarte cómo lo hacemos?
            </h2>
            
            <p className="text-slate-300 mb-8">
              Sin compromiso. Te contamos nuestra metodología y cómo podemos ayudarte a conseguir hasta un <strong className="text-amber-400">+50% más</strong> en tus certificados CAES.
            </p>
            
            <button
              onClick={handleCTAClick}
              className="w-full md:w-auto px-10 py-5 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 rounded-xl font-bold text-lg text-white shadow-xl shadow-emerald-500/25 transition-all transform hover:scale-105 flex items-center justify-center gap-3 mx-auto"
              data-testid="cta-reunion-btn"
            >
              Quiero mi reunión gratuita <ChevronRight className="w-6 h-6" />
            </button>
            
            {informe.comercial_nombre && (
              <div className="mt-8 pt-6 border-t border-emerald-500/20">
                <p className="text-slate-400 text-sm mb-2">Tu contacto directo:</p>
                <p className="text-white font-semibold">{informe.comercial_nombre}</p>
                {informe.comercial_email && (
                  <a href={`mailto:${informe.comercial_email}`} className="text-emerald-400 text-sm flex items-center justify-center gap-2 mt-1 hover:underline">
                    <Mail className="w-4 h-4" /> {informe.comercial_email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-emerald-500/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-8 mx-auto mb-4 opacity-60" />
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <a 
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-500/60 hover:text-slate-400 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-slate-500/30">|</span>
            <a 
              href="https://www.mobilsafe.es/politica-de-cookies/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-500/60 hover:text-slate-400 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 MOBILSAFE Fleet Technology. Comprometidos con la movilidad sostenible.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InformeCAES;
